import { Injectable, OnDestroy } from '@angular/core';
import { ISourceSiteWithFiles } from '@features/admin/pages/site-detail/@shared/models/source-site-with-files.interface';
import {
  BatchQueryResultViewModel,
  CreateSourceSiteCommand,
  EditSourceSiteCommand,
  IQualifiedPersonViewModel,
  ISourceSiteSearchViewModelSiteTotalTypePagedResultResponse,
  ISourceSiteUserSearchViewModel,
  ISourceSiteViewModel,
  SiteTotalType,
  SourceSiteAttachmentViewModel,
  SourceSiteQPSearchViewModelTotalTypePagedResultResponse,
  SourceSiteUserSearchViewModelTotalTypePagedResultResponse,
  TotalType
} from '@services/cleanfill-api/cleanfill-api.generated';
import { HttpSourceSitesService } from '@services/cleanfill-core/http-source-sites/http-source-sites.service';
import { ISearchQuery } from '@view-models/interfaces/search-query';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class StoreSourceSitesService implements OnDestroy {
  private sourceSiteSubj$ = new BehaviorSubject<ISourceSiteViewModel>(null);
  sourceSite$ = this.sourceSiteSubj$.asObservable();
  sourceSiteWithFiles$: Observable<ISourceSiteWithFiles> = this.sourceSite$.pipe(
    filter(Boolean),
    switchMap((site: ISourceSiteViewModel) =>
      this.httpSrcSite.getAttachments(site.uniqueId).pipe(map((attachments) => ({ ...site, files: attachments })))
    )
  );

  private sourceSitePagedResultSubj$ = new BehaviorSubject<ISourceSiteSearchViewModelSiteTotalTypePagedResultResponse>(null);
  sourceSitePagedResult$ = this.sourceSitePagedResultSubj$.asObservable();
  sourceSites$ = this.sourceSitePagedResult$.pipe(map((result) => result?.records || []));
  totalSourceSites$ = this.sourceSitePagedResult$.pipe(
    map((v) => v?.totals?.find((total) => total.type === SiteTotalType.AllStatus).value || 0)
  );

  private srcSiteQpsSubj$ = new BehaviorSubject<IQualifiedPersonViewModel[]>([]);
  srcSiteQps$ = this.srcSiteQpsSubj$.asObservable();
  srcSiteQpCompanies$ = this.srcSiteQps$;

  private sourceSiteBatchesSubj$ = new BehaviorSubject<BatchQueryResultViewModel[]>([]);
  sourceSiteBatches$ = this.sourceSiteBatchesSubj$.asObservable();

  private createdSourceSiteSubj$ = new BehaviorSubject<ISourceSiteViewModel>(null);
  createdSourceSite$ = this.createdSourceSiteSubj$.asObservable();
  createdSourceSiteUniqueId$ = this.createdSourceSite$.pipe(map((srcSite) => srcSite?.uniqueId));

  private filesUploadedSubj$ = new BehaviorSubject<number>(0);
  filesUploaded$ = this.filesUploadedSubj$.asObservable();

  private filesSubj$ = new BehaviorSubject<SourceSiteAttachmentViewModel[]>([]);
  files$ = this.filesSubj$.asObservable();

  private usersPagedResultSubj$ = new BehaviorSubject<SourceSiteUserSearchViewModelTotalTypePagedResultResponse>(null);
  usersPagedResult$ = this.usersPagedResultSubj$.asObservable();
  users$ = this.usersPagedResult$.pipe(map((result) => result?.records || []));
  totalUsers$ = this.usersPagedResult$.pipe(map((v) => v?.totals?.find((total) => total.type === TotalType.Total).value || 0));

  private qpPagedResultSubj$ = new BehaviorSubject<SourceSiteQPSearchViewModelTotalTypePagedResultResponse>(null);
  qpPagedResult$ = this.qpPagedResultSubj$.asObservable();
  qps$ = this.qpPagedResult$.pipe(map((result) => result?.records || []));
  totalQps$ = this.qpPagedResult$.pipe(map((v) => v?.totals?.find((total) => total.type === TotalType.Total).value || 0));

  subscriptions = new Subscription();

  constructor(private httpSrcSite: HttpSourceSitesService) { }

  loadSourceSitesById(uniqueId: string, query: ISearchQuery): void {
    this.httpSrcSite
      .getSourceSitesById(uniqueId, query)
      .subscribe((result: ISourceSiteSearchViewModelSiteTotalTypePagedResultResponse) => this.sourceSitePagedResultSubj$.next(result));
  }
  loadSourceSites(query: ISearchQuery): void {
    this.httpSrcSite
      .getSourceSites(query)
      .subscribe((result: ISourceSiteSearchViewModelSiteTotalTypePagedResultResponse) => this.sourceSitePagedResultSubj$.next(result));
  }
  loadAllSourceSites(query: ISearchQuery): void {
    this.httpSrcSite
      .getALLSourceSites(query)
      .subscribe((result: ISourceSiteSearchViewModelSiteTotalTypePagedResultResponse) => this.sourceSitePagedResultSubj$.next(result));
  }

  loadSourceSiteQps(srcSiteUniqueId: string) {
    const sub = this.httpSrcSite.getSourceSiteQps(srcSiteUniqueId).subscribe((res) => this.srcSiteQpsSubj$.next(res || []));
    this.subscriptions.add(sub);
  }

  loadBatches(sourceSiteUniqueId: string) {
    this.httpSrcSite
      .getBatches(sourceSiteUniqueId)
      .subscribe((result: BatchQueryResultViewModel[]) => this.sourceSiteBatchesSubj$.next(result));
  }

  createSourceSite(payload: CreateSourceSiteCommand, fn = () => { }): void {
    const sub = this.httpSrcSite.createSourceSite(payload).subscribe((srcSite) => {
      this.createdSourceSiteSubj$.next(srcSite);
      fn();
    });
    this.subscriptions.add(sub);
  }

  editSourceSite(payload: EditSourceSiteCommand, fn = () => { }): void {
    const sub = this.httpSrcSite.editSourceSite(payload).subscribe(fn);
    this.subscriptions.add(sub);
  }

  uploadAttachments(data: FormData, fn = () => { }): void {
    const sub = this.httpSrcSite.uploadAttachments(data).subscribe((totalUploaded) => {
      this.filesUploadedSubj$.next(totalUploaded);
      fn();
    });
    this.subscriptions.add(sub);
  }

  getAttachments(uniqueId: string): void {
    const sub = this.httpSrcSite.getAttachments(uniqueId).subscribe((files) => this.filesSubj$.next(files || []));
    this.subscriptions.add(sub);
  }

  clearNewlyCreatedSourceSite() {
    this.createdSourceSiteSubj$.next(undefined);
  }

  loadSourceSite(uniqueId: string) {
    return this.httpSrcSite
      .getSourceSite(uniqueId)
      .pipe(
        filter(Boolean),
        switchMap((site: ISourceSiteViewModel) =>
          this.httpSrcSite.getAttachments(site.uniqueId).pipe(map((attachments) => ({ ...site, files: attachments })))
        )
      )
      .toPromise()
      .then((site) => {
        this.sourceSiteSubj$.next(site);
        return site;
      });
  }

  loadUsers(sourceSiteUniqueId: string, query: ISearchQuery, fn = () => { }) {
    const sub = this.httpSrcSite.getSourceSiteUsers(sourceSiteUniqueId, query).subscribe((result) => {
      this.usersPagedResultSubj$.next(result);
      fn();
    });
    this.subscriptions.add(sub);
  }

  loadAssignedQps(sourceSiteUniqueId: string, query: ISearchQuery, fn = () => { }) {
    const sub = this.httpSrcSite.getSourceSiteAssignedQps(sourceSiteUniqueId, query).subscribe((result) => {
      this.qpPagedResultSubj$.next(result);
      fn();
    });
    this.subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  unassignUser(userUniqueId: string, fn = () => { }) {
    const sub = this.httpSrcSite.unassignUser(userUniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }

  assignUser(sourceSiteUniqueId: string, user: ISourceSiteUserSearchViewModel, fn = () => { }) {
    const sub = this.httpSrcSite.assignUser(sourceSiteUniqueId, user.uniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }

  unassignQp(qpUniqueId: string, fn = () => { }): void {
    const sub = this.httpSrcSite.unassignQp(qpUniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }

  assignQp(sourceSiteUniqueId, qpUniqueId: string, fn = () => { }) {
    const sub = this.httpSrcSite.assignQp(sourceSiteUniqueId, qpUniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }

  deleteSourceSite(uniqueId: string, fn: () => void) {
    const sub = this.httpSrcSite.deleteSourceSite(uniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }

  archiveSourceSite(uniqueId: string, fn: () => void) {
    const sub = this.httpSrcSite.archiveSourceSite(uniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }

  reactivateSourceSite(uniqueId: string, fn: () => void) {
    const sub = this.httpSrcSite.reactivateSourceSite(uniqueId).subscribe(fn);
    this.subscriptions.add(sub);
  }

  clearSourceSite(): void {
    this.sourceSiteSubj$.next(null);
  }

  clearSourceSites(): void {
    this.sourceSitePagedResultSubj$.next(null);
  }
}
